import { useEffect, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cx } from "./ui.js";

export interface SwipeAction { key: string; label: string; icon: LucideIcon; tone: "success" | "warning" | "danger" | "info" | "muted"; onSelect: () => void }

const toneBg: Record<SwipeAction["tone"], string> = {
  success: "bg-success", warning: "bg-p2", danger: "bg-danger", info: "bg-info", muted: "bg-text-faint",
};

const ACTION_W = 76;
const COMMIT_RIGHT = 88;
let closeOpenRow: (() => void) | null = null;

const vibrate = (ms: number) => { try { navigator.vibrate?.(ms); } catch { /* unsupported */ } };

/**
 * Touch swipe actions for a row (pointer events, no library):
 * swipe right past the threshold runs `right` (e.g. complete); swipe left reveals
 * `left` actions, and a long swipe left runs `fullLeft` (e.g. delete). A mostly
 * vertical gesture is left to the browser for scrolling. Holding still for 450ms
 * fires `onLongPress`. Mouse input is ignored so desktop drag and click still work.
 */
export function SwipeRow({ children, right, left = [], fullLeft, onLongPress, enabled = true, className, contentClassName }: {
  children: ReactNode; right?: SwipeAction; left?: SwipeAction[]; fullLeft?: () => void; onLongPress?: () => void; enabled?: boolean; className?: string; contentClassName?: string;
}) {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [open, setOpen] = useState(false);
  const g = useRef<{ x: number; y: number; id: number; mode: "pending" | "swipe" | "scroll"; base: number; timer?: number; long: boolean } | null>(null);
  const suppressClick = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const openW = left.length * ACTION_W;

  const settle = (to: number) => { setAnimating(true); setDx(to); window.setTimeout(() => setAnimating(false), 220); };
  const close = () => { setOpen(false); settle(0); };

  useEffect(() => () => { if (g.current?.timer) window.clearTimeout(g.current.timer); }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled || e.pointerType === "mouse" || !e.isPrimary) return;
    if ((e.target as HTMLElement).closest("[data-swipe-ignore]")) return;
    const state = { x: e.clientX, y: e.clientY, id: e.pointerId, mode: "pending" as const, base: open ? -openW : 0, long: false, timer: undefined as number | undefined };
    if (onLongPress) {
      state.timer = window.setTimeout(() => {
        if (g.current?.mode === "pending") { g.current.long = true; suppressClick.current = true; vibrate(12); onLongPress(); }
      }, 450);
    }
    g.current = state;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = g.current;
    if (!s || e.pointerId !== s.id || s.long) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (s.mode === "pending") {
      if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) { s.mode = "scroll"; window.clearTimeout(s.timer); return; }
      if (Math.abs(mx) > 10 && Math.abs(mx) > Math.abs(my)) {
        s.mode = "swipe";
        window.clearTimeout(s.timer);
        if (closeOpenRow && closeOpenRow !== close) closeOpenRow();
        try { rootRef.current?.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
      } else return;
    }
    if (s.mode !== "swipe") return;
    let next = s.base + mx;
    if (next > 0 && !right) next = next / 4;
    if (next < 0 && !left.length && !fullLeft) next = next / 4;
    setDx(next);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const s = g.current;
    g.current = null;
    if (!s || e.pointerId !== s.id) return;
    window.clearTimeout(s.timer);
    if (s.long) return;
    if (s.mode !== "swipe") {
      if (open) { suppressClick.current = true; close(); }
      return;
    }
    suppressClick.current = true;
    const width = rootRef.current?.offsetWidth || 360;
    const final = s.base + (e.clientX - s.x);
    if (final > COMMIT_RIGHT && right) { vibrate(10); settle(0); setOpen(false); right.onSelect(); return; }
    if (final < -width * 0.6 && fullLeft) { vibrate(16); settle(-width); window.setTimeout(() => { fullLeft(); setDx(0); setOpen(false); }, 200); return; }
    if (final < -40 && left.length) { settle(-openW); setOpen(true); closeOpenRow = close; return; }
    close();
  };

  const onClickCapture = (e: React.MouseEvent) => {
    // Swallow only the click the browser synthesises right after a gesture; taps on revealed actions always go through.
    if (!suppressClick.current) return;
    suppressClick.current = false;
    if ((e.target as HTMLElement).closest("[data-swipe-ignore]")) return;
    e.preventDefault();
    e.stopPropagation();
  };
  useEffect(() => {
    if (!dx && !open) return;
    const t = window.setTimeout(() => { suppressClick.current = false; }, 350);
    return () => window.clearTimeout(t);
  }, [dx, open]);

  const showRight = dx > 0 && right;
  const RightIcon = right?.icon;
  return (
    <div ref={rootRef} className={cx("relative", enabled && "overflow-hidden", className)} style={{ touchAction: enabled ? "pan-y" : undefined }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { const s = g.current; g.current = null; if (s) window.clearTimeout(s.timer); if (s?.mode === "swipe") close(); }}
      onClickCapture={onClickCapture} onContextMenu={(e) => { if (onLongPress && e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === "touch") e.preventDefault(); }}>
      {showRight && RightIcon && (
        <div className={cx("absolute inset-0 flex items-center gap-2 pl-5 text-body font-semibold text-white", toneBg[right.tone])} aria-hidden>
          <RightIcon size={20} style={{ transform: `scale(${dx > COMMIT_RIGHT ? 1.15 : 0.9})`, transition: "transform 150ms" }} />{dx > COMMIT_RIGHT ? right.label : ""}
        </div>
      )}
      {dx < 0 && (
        <div className="absolute inset-y-0 right-0 flex" style={{ width: Math.max(openW, -dx) }}>
          {left.map((a, i) => {
            const Icon = a.icon;
            const isLast = i === left.length - 1;
            return (
              <button key={a.key} type="button" data-swipe-ignore onClick={(e) => { e.stopPropagation(); close(); a.onSelect(); }} aria-label={a.label}
                className={cx("flex h-full flex-col items-center justify-center gap-1 text-caption font-semibold normal-case tracking-normal text-white", toneBg[a.tone])}
                style={{ width: isLast ? undefined : ACTION_W, flex: isLast ? 1 : undefined }}>
                <Icon size={20} aria-hidden />{a.label}
              </button>
            );
          })}
        </div>
      )}
      <div className={cx("relative bg-surface", contentClassName)} style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: animating ? "transform 220ms var(--ease-spring)" : undefined }}>
        {children}
      </div>
    </div>
  );
}
